import { useCallback } from 'react';
import {
  useQuery,
  useSuspenseQuery,
  QueryResult,
  useLazyQuery,
  useApolloClient,
  QueryOptions,
  QueryHookOptions,
  SuspenseQueryHookOptions,
  LazyQueryHookOptions,
  LazyQueryExecFunction
} from '@apollo/client';
import { useSnackbarHelper } from '../../useSnackbarHelper';
import QUERY from '../operations/AllUsers.graphql';
import { Query, QueryallUsersArgs } from '../../__generated__/globalTypes';

export const useAllUsersQuery = (options?: QueryHookOptions<Query, QueryallUsersArgs>) => {
  const { t, enqueueSnackbar } = useSnackbarHelper();
  const result = useQuery<Query, QueryallUsersArgs>(QUERY, {
    onError: (error) => {
      enqueueSnackbar(t(error.message), { variant: 'error' });
    },
    ...options
  });

  return result;
};

export const useAllUsersLazyQuery = (
  options?: LazyQueryHookOptions<Query, QueryallUsersArgs>
): QueryResult<Query, QueryallUsersArgs> & { getAllUsers: LazyQueryExecFunction<Query, QueryallUsersArgs> } => {
  const { t, enqueueSnackbar } = useSnackbarHelper();
  const [getAllUsers, result] = useLazyQuery<Query, QueryallUsersArgs>(QUERY, {
    onError: (error) => {
      enqueueSnackbar(t(error.message), { variant: 'error' });
    },
    ...options
  });

  return { getAllUsers, ...result };
};

export const useAllUsersPromiseQuery = () => {
  const client = useApolloClient();

  const getAllUsers = useCallback(
    (options?: Omit<QueryOptions<QueryallUsersArgs, Query>, 'query'>) =>
      client.query<Query, QueryallUsersArgs>({ ...options, query: QUERY }),
    [client]
  );

  return { getAllUsers };
};
// Suspense Query Hooks
export const useAllUsersSuspenseQuery = (options?: SuspenseQueryHookOptions<Query, QueryallUsersArgs>) => {
  const result = useSuspenseQuery<Query, QueryallUsersArgs>(QUERY, {
    ...options
  });

  return result;
};
